import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {ProcessesPluginMetadataEditorItem} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import FlowchartContextPanel, {
  getStyleForSidepanelSize,
  PanelSide,
  ContextPanelContent,
  SidepanelSize,
  sizeToWidth,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import AddNodeSidepanelContent from './left-sidepanel/AddNodeSidepanelContent';
import {UserFacingNodeTypes} from './processFlowchartConfigEditorConstants';
import {FlowchartOptions} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';
import {ApplyFlowchartChangesFn} from './useFlowchartState';

export const LEFT_SIDEPANEL_SIZE = SidepanelSize.EXTRA_SMALL;
export const LEFT_SIDEPANEL_WIDTH = sizeToWidth[LEFT_SIDEPANEL_SIZE] as number;
export interface LeftSidepanelContent {
  icon: BenchlingIconName;
  name: string;
  content: JSX.Element;
  width: SidepanelSize;
}

export interface ProcessFlowchartMetadataEditorProps {
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
}

export interface LeftSidepanelProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  allowedNodeTypes: Array<UserFacingNodeTypes>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  options: FlowchartOptions;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  metadataSidepanel?: ProcessesPluginMetadataEditorItem;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  setPlaceholderNodeBeingAdded: (value: string | null) => void;
}

// TODO(BNCH-126721): Consolidate with transition modal left sidepanel
export default function ProcessFlowchartConfigEditorLeftSidepanel(props: LeftSidepanelProps): JSX.Element {
  const [selectedSidebarIconIndex, setSelectedSidebarIconIndex] = useState<number | null>(null);
  const locked = ReactFlowLockedContext.useContext() ?? false;

  const sidepanelContents: Array<LeftSidepanelContent> = [];

  if (
    !locked &&
    props.flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL)
  ) {
    sidepanelContents.push({
      icon: 'process_task_schema',
      name: 'Add Nodes',
      content: (
        <AddNodeSidepanelContent
          allowedNodeTypes={props.allowedNodeTypes}
          taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
          applyFlowchartChanges={props.applyFlowchartChanges}
          options={props.options}
          rootTaskSchema={props.rootTaskSchema}
          flowchartEditorConfigurator={props.flowchartEditorConfigurator}
          reactFlowWrapper={props.reactFlowWrapper}
          setPlaceholderNodeBeingAdded={props.setPlaceholderNodeBeingAdded}
        />
      ),
      width: SidepanelSize.EXTRA_SMALL,
    });
  }

  if (props.metadataSidepanel) {
    sidepanelContents.push({
      icon: props.metadataSidepanel.icon,
      name: props.metadataSidepanel.name,
      content: (
        <props.metadataSidepanel.editorComponent
          flowchartTaskSchema={props.rootTaskSchema}
          flowchartConfigMetadata={props.flowchartConfigMetadata}
          applyFlowchartChanges={props.applyFlowchartChanges}
        />
      ),
      width: props.metadataSidepanel.width,
    });
  }

  const {setIsOpen} = props;

  const selectedSidepanelContent =
    selectedSidebarIconIndex != null ? sidepanelContents[selectedSidebarIconIndex] : null;

  return (
    <div
      className={css(
        styles.leftSidepanelContainer,
        props.isOpen &&
          selectedSidepanelContent?.width === SidepanelSize.FULL &&
          getStyleForSidepanelSize(SidepanelSize.FULL)
      )}
    >
      <FlowchartSidebar
        icons={sidepanelContents.map((content, index) => ({
          name: content.icon,
          onClick: () => {
            setSelectedSidebarIconIndex(index);
            setIsOpen(true);
          },
          tooltip: content.name,
          tooltipPlacement: FloatingPlacement.RIGHT,
        }))}
        selectedIconIndex={selectedSidebarIconIndex}
        setSelectedIconIndex={setSelectedSidebarIconIndex}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        size={selectedSidepanelContent?.width ?? LEFT_SIDEPANEL_SIZE}
        sidepanelName={selectedSidepanelContent?.name ?? ''}
        sidepanelIcon={selectedSidepanelContent?.icon ?? 'wand-magic-sparkles'}
        isOpen={props.isOpen}
        onClose={() => {
          props.setIsOpen(false);
          setSelectedSidebarIconIndex(null);
        }}
      >
        <ContextPanelContent>{selectedSidepanelContent?.content}</ContextPanelContent>
      </FlowchartContextPanel>
    </div>
  );
}

const styles = StyleSheet.create({
  leftSidepanelContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    height: '100%',
    zIndex: 10,
  },
});
